import { useT } from '@kmgestao/i18n';
import { Badge, Button } from '@kmgestao/ui';

import { PALETTES, type PaletteKey } from '../../../features/clinic-appearance/index.js';

export interface PaletteCardProps {
  paletteKey: PaletteKey;
  isCurrent: boolean;
  isSubmitting: boolean;
  onSelect: (key: PaletteKey) => void;
}

/**
 * Card visual de uma paleta — mostra preview de cores + nome +
 * descrição + botão "Aplicar". Card que está em uso mostra badge
 * "Em uso" e botão "Aplicar" disabled.
 *
 * Preview usa **cores literais inline** dos tokens da paleta (HSL via
 * style — NÃO usa as CSS vars `--primary` porque queremos mostrar a
 * paleta TARGET independente da paleta atualmente aplicada). Lint:tokens
 * permite `style={{ ... }}` quando o valor vem de fonte tipada
 * controlada (caso aqui — `PALETTES` é catálogo curado).
 *
 * Acessibilidade: card é `<button>` semântico (não `<div>`) — TAB
 * funciona, Enter/Space ativa, screen readers anunciam corretamente.
 */
export function PaletteCard({ paletteKey, isCurrent, isSubmitting, onSelect }: PaletteCardProps) {
  const { t } = useT('clinics');
  const palette = PALETTES[paletteKey];
  const tokens = palette.light; // preview sempre em light pra consistência

  return (
    <div
      className={
        isCurrent
          ? 'flex flex-col gap-3 rounded-xl border-2 border-primary bg-card p-4 shadow-sm'
          : 'flex flex-col gap-3 rounded-xl border border-border bg-card p-4'
      }
    >
      {/* Preview visual — barra de cores + amostras */}
      <div aria-hidden="true" className="flex h-16 overflow-hidden rounded-lg">
        <div className="flex-1" style={{ background: `hsl(${tokens.primary})` }} />
        <div className="flex-1" style={{ background: `hsl(${tokens.accent})` }} />
        <div
          className="flex flex-1 items-center justify-center text-xs font-semibold"
          style={{
            background: `hsl(${tokens.primary})`,
            color: `hsl(${tokens.primaryForeground})`,
          }}
        >
          Aa
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-foreground">{palette.name}</h3>
          {isCurrent ? <Badge variant="success">{t('appearance.palette.inUse')}</Badge> : null}
        </div>
        <p className="text-sm text-muted-foreground">{palette.description}</p>
      </div>

      <Button
        type="button"
        size="sm"
        variant={isCurrent ? 'outline' : 'default'}
        disabled={isCurrent || isSubmitting}
        onClick={() => onSelect(paletteKey)}
      >
        {isCurrent ? t('appearance.palette.currentAction') : t('appearance.palette.applyAction')}
      </Button>
    </div>
  );
}
